/**
 * @fileoverview Run Comparison
 * @description Side-by-side diff between the latest run and a chosen baseline.
 */

import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeftRight, ArrowRight, GitCompare } from 'lucide-react';
import { compareRuns, formatDelta } from '../utils/runComparison';
import { ScoreChip } from './common';
import { BAND_COLORS } from '../constants';
import { bandForScore } from '../utils/evaluationView';

const STATUS_STYLE = {
  improved: { label: 'Improved', color: BAND_COLORS.green },
  regressed: { label: 'Regressed', color: BAND_COLORS.red },
  unchanged: { label: 'Unchanged', color: '#5B6573' },
  added: { label: 'New', color: '#0097A9' },
  removed: { label: 'Removed', color: '#8B5CF6' },
  'not-run': { label: 'Not run', color: '#5B6573' },
};

const TALLY_ORDER = [
  ['improved', 'Improved'],
  ['regressed', 'Regressed'],
  ['unchanged', 'Unchanged'],
  ['added', 'New'],
  ['removed', 'Removed'],
];

/**
 * Format a run's timestamp for the picker.
 * @param {Object} run - Stored run.
 * @returns {string} Localized date.
 */
function runDate(run) {
  return new Date(run.generatedAt || run.importedAt).toLocaleDateString();
}

/**
 * Render a labelled run picker.
 * @param {Object} props - Label, run list, selection, and change handler.
 * @returns {JSX.Element} Run picker.
 */
function RunSelect({ label, runs, value, excludedId, onChange }) {
  const selected = runs.find((run) => run.id === value) || null;

  return (
    <Box className="aier-compare-slot">
      <Typography className="aier-metric-label">{label}</Typography>
      <FormControl size="small" fullWidth>
        <Select
          displayEmpty
          value={selected?.id || ''}
          onChange={(event) => onChange(event.target.value)}
          className="aier-compare-select"
          inputProps={{ 'aria-label': `${label} run` }}
          MenuProps={{ slotProps: { paper: { sx: { maxHeight: 340, maxWidth: 420 } } } }}
          renderValue={() => (selected
            ? (
              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                <Typography className="aier-compare-run">{selected.label}</Typography>
                <Typography className="aier-compare-run-date">{runDate(selected)}</Typography>
              </Stack>
            )
            : <Typography className="aier-compare-placeholder">Select a run…</Typography>
          )}
        >
          {[...runs].reverse().map((run) => (
            <MenuItem
              key={run.id}
              value={run.id}
              disabled={run.id === excludedId}
              className="aier-compare-option"
            >
              <ListItemText
                primary={run.label}
                secondary={`${runDate(run)} · ${run.summary.overallAverage.toFixed(2)} overall`}
                slotProps={{
                  primary: { className: 'aier-compare-option-primary' },
                  secondary: { className: 'aier-compare-option-secondary' },
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selected && (
        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
          <ScoreChip
            score={selected.summary.overallAverage}
            band={bandForScore(selected.summary.overallAverage)}
          />
          <Typography className="aier-metric-caption">overall</Typography>
        </Stack>
      )}
    </Box>
  );
}

/**
 * Render a comparison between any two runs.
 * @param {Object} props - Runs, both selections, change handlers, and swap handler.
 * @returns {JSX.Element} Comparison panel.
 */
export function RunComparison({ runs, baselineId, currentId, onBaselineChange, onCurrentChange, onSwap }) {
  const baseline = runs.find((run) => run.id === baselineId) || null;
  const current = runs.find((run) => run.id === currentId) || null;
  const isSameRun = Boolean(baseline && current && baseline.id === current.id);
  const comparison = isSameRun ? null : compareRuns(current, baseline);

  /**
   * Describe why no comparison is shown.
   * @returns {string} Placeholder message.
   */
  const placeholderMessage = () => {
    if (runs.length < 2) {
      return 'A second run is needed before results can be compared.';
    }
    if (isSameRun) {
      return 'Select two different runs to compare.';
    }
    return 'Select a run on each side to compare them.';
  };

  return (
    <Paper className="aier-compare" elevation={0}>
      <Box className="aier-compare-header">
        <Typography className="aier-eyebrow">Run comparison</Typography>
        <Typography variant="h6">Compare runs</Typography>
        <Typography className="aier-compare-subtitle">
          Pick any two runs to see what improved or regressed between them.
        </Typography>
      </Box>

      <Box className="aier-compare-picker">
        <RunSelect
          label="Baseline"
          runs={runs}
          value={baselineId}
          excludedId={currentId}
          onChange={onBaselineChange}
        />

        <Box className="aier-compare-swap">
          <Tooltip title="Swap runs">
            <IconButton
              size="small"
              onClick={onSwap}
              disabled={!baseline || !current || isSameRun}
              aria-label="Swap baseline and comparison runs"
            >
              <ArrowLeftRight size={16} />
            </IconButton>
          </Tooltip>
        </Box>

        <RunSelect
          label="Compare against"
          runs={runs}
          value={currentId}
          excludedId={baselineId}
          onChange={onCurrentChange}
        />
      </Box>

      {!comparison ? (
        <Box className="aier-compare-placeholder-panel">
          <GitCompare size={22} color="#8B97A3" />
          <Typography color="text.secondary">{placeholderMessage()}</Typography>
        </Box>
      ) : (
        <>
          <Box className="aier-compare-movement">
            <Box className="aier-movement-headline">
              <Typography className="aier-metric-label">Overall average</Typography>
              <Stack direction="row" spacing={1.25} alignItems="center" mt={0.5}>
                <Typography className="aier-movement-score">
                  {baseline.summary.overallAverage.toFixed(2)}
                </Typography>
                <ArrowRight size={16} color="#8B97A3" />
                <Typography className="aier-movement-score aier-movement-score--current">
                  {current.summary.overallAverage.toFixed(2)}
                </Typography>
                <Chip
                  size="small"
                  label={formatDelta(comparison.deltaOverall)}
                  className="aier-movement-delta"
                  sx={{
                    color: comparison.deltaOverall >= 0 ? BAND_COLORS.green : BAND_COLORS.red,
                    backgroundColor: `${comparison.deltaOverall >= 0 ? BAND_COLORS.green : BAND_COLORS.red}16`,
                  }}
                />
              </Stack>
              <Typography className="aier-metric-caption">
                {comparison.deltaBelowTarget === 0
                  ? 'No change in the number of results below target.'
                  : `${Math.abs(comparison.deltaBelowTarget)} ${comparison.deltaBelowTarget > 0 ? 'more' : 'fewer'} below target.`}
              </Typography>
            </Box>

            <Box className="aier-tally-row">
              {TALLY_ORDER.map(([key, label]) => (
                <Box key={key} className="aier-tally" sx={{ '--tally-color': STATUS_STYLE[key].color }}>
                  <Typography className="aier-tally-value">{comparison[key]}</Typography>
                  <Typography className="aier-tally-label">{label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {comparison.changedRows.length === 0 ? (
            <Box className="aier-no-results">
              <Typography color="text.secondary">
                Every result scored the same as the baseline run.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" className="aier-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Result</TableCell>
                    <TableCell>Evaluator</TableCell>
                    <TableCell align="center">Baseline</TableCell>
                    <TableCell align="center">Current</TableCell>
                    <TableCell align="center">Change</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparison.changedRows.map((item) => {
                    const style = STATUS_STYLE[item.status];
                    return (
                      <TableRow key={item.key} hover>
                        <TableCell className="aier-cell-field">{item.row.field}</TableCell>
                        <TableCell className="aier-cell-muted">{item.row.evaluator}</TableCell>
                        <TableCell align="center" className="aier-cell-score">
                          {item.baselineScore === null ? '—' : item.baselineScore.toFixed(2)}
                        </TableCell>
                        <TableCell align="center" className="aier-cell-score">
                          {item.currentScore === null ? '—' : item.currentScore.toFixed(2)}
                        </TableCell>
                        <TableCell align="center" className="aier-cell-score" sx={{ color: style.color }}>
                          {formatDelta(item.delta)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={style.label}
                            sx={{ color: style.color, backgroundColor: `${style.color}16`, fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Paper>
  );
}

export default RunComparison;
